"use client";
import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Pause, WifiOff, AlertTriangle, Loader2 } from "lucide-react";
import { useTheme } from "next-themes";
import { cn } from "@/lib/utils";
import { useMicrophoneState } from "@/contexts/MicrophoneContext";
import { SessionConnectionStatus } from "@/services/interfaces/Session";

type Props = {
  className?: string;
  isRecording: boolean;
  sessionConnectionStatus: SessionConnectionStatus;
  onPause: () => void;
  isLoading?: boolean;
};

export const AudioRecorderWithVisualizer = ({
  className,
  sessionConnectionStatus,
  isRecording,
  onPause,
  isLoading = false,
}: Props) => {
  const { theme } = useTheme();
  const { microphoneService } = useMicrophoneState();

  // Refs for audio analysis
  const audioAnalyserRef = useRef<{
    audioContext: AudioContext | null;
    analyser: AnalyserNode | null;
    source: MediaStreamAudioSourceNode | null;
  }>({
    audioContext: null,
    analyser: null,
    source: null,
  });
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<any>(null);

  // Setup audio analysis when recording starts
  useEffect(() => {
    console.log('[🎵 VISUALIZER] isRecording:', isRecording, microphoneService);
    if (isRecording && microphoneService) {
      const setupAudioAnalysis = async () => {
        console.log('[🎵 VISUALIZER] Setting up audio analysis');
        try {
          // Get the current media stream from microphone service
          const stream = microphoneService.getMediaStream();
          console.log('[🎵 VISUALIZER] Stream:', stream);
          if (!stream) return;

          // 验证流的状态和轨道
          console.log('[🎵 VISUALIZER] Setting up audio analysis with stream:', {
            streamId: stream.id,
            audioTracks: stream.getAudioTracks().length,
            trackStates: stream.getAudioTracks().map(track => ({
              id: track.id,
              enabled: track.enabled,
              readyState: track.readyState,
              muted: track.muted
            }))
          });

          // Create audio context and analyser
          const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
          const audioContext = new AudioContext();
          const analyser = audioContext.createAnalyser();
          const source = audioContext.createMediaStreamSource(stream);
          
          // Configure analyser
          analyser.fftSize = 256;
          source.connect(analyser);
          
          console.log('[🎵 VISUALIZER] Audio analysis setup completed:', {
            audioContextState: audioContext.state,
            analyserConnected: true,
            fftSize: analyser.fftSize
          });
          
          audioAnalyserRef.current = {
            audioContext,
            analyser,
            source,
          };
        } catch (error) {
          console.error('[🎵 VISUALIZER] Failed to setup audio analysis:', error);
        }
      };

      setupAudioAnalysis();
    } else {
      // 立即清理音频分析资源当录制停止时
      cleanupAudioAnalysis();
    }

    return () => {
      // Cleanup on unmount
      cleanupAudioAnalysis();
    };
  }, [isRecording, microphoneService]);

  // 提取清理函数以确保一致的资源释放
  const cleanupAudioAnalysis = () => {
    const { audioContext, analyser, source } = audioAnalyserRef.current;
    
    console.log('[🎵 VISUALIZER] Starting cleanup of audio analysis resources');
    
    // 停止动画帧
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    
    // 断开音频节点连接
    if (source) {
      try {
        source.disconnect();
        console.log('[🎵 VISUALIZER] Source node disconnected');
      } catch (e) {
        console.warn('[🎵 VISUALIZER] Error disconnecting source:', e);
      }
    }
    
    if (analyser) {
      try {
        analyser.disconnect();
        console.log('[🎵 VISUALIZER] Analyser node disconnected');
      } catch (e) {
        console.warn('[🎵 VISUALIZER] Error disconnecting analyser:', e);
      }
    }
    
    // 关闭音频上下文
    if (audioContext && audioContext.state !== 'closed') {
      audioContext.close().catch(e => {
        console.warn('[🎵 VISUALIZER] Error closing audio context:', e);
      }).then(() => {
        console.log('[🎵 VISUALIZER] Audio context closed');
      });
    }
    
    // 重置引用
    audioAnalyserRef.current = {
      audioContext: null,
      analyser: null,
      source: null,
    };
    
    // 清理画布
    const canvas = canvasRef.current;
    if (canvas) {
      const canvasCtx = canvas.getContext("2d");
      if (canvasCtx) {
        console.log('[🎵 VISUALIZER] Clearing canvas');
        canvasCtx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
    
    console.log('[🎵 VISUALIZER] Audio analysis cleanup completed');
  };

  // 设置canvas分辨率以匹配显示尺寸
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const updateCanvasSize = () => {
      const rect = canvas.getBoundingClientRect();
      
      // 直接设置canvas内部分辨率为显示尺寸，不使用devicePixelRatio
      canvas.width = rect.width;
      canvas.height = rect.height;
      
      // console.log('[🎵 CANVAS RESIZE] Canvas size updated:', {
      //   displayWidth: rect.width,
      //   displayHeight: rect.height,
      //   internalWidth: canvas.width,
      //   internalHeight: canvas.height
      // });
    };

    // 初始设置
    updateCanvasSize();

    // 监听窗口大小变化
    window.addEventListener('resize', updateCanvasSize);
    
    // 使用ResizeObserver监听canvas容器大小变化
    const resizeObserver = new ResizeObserver(updateCanvasSize);
    resizeObserver.observe(canvas);

    return () => {
      window.removeEventListener('resize', updateCanvasSize);
      resizeObserver.disconnect();
    };
  }, []);

  // Visualizer
  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const canvasCtx = canvas.getContext("2d");
    
    const drawWaveform = (dataArray: Uint8Array) => {
      if (!canvasCtx) return;
      
      console.log('[🎵 VISUALIZER] Drawing waveform');
      // 每次绘制时获取最新的canvas尺寸
      const WIDTH = canvas.width;
      const HEIGHT = canvas.height;
      
      canvasCtx.clearRect(0, 0, WIDTH, HEIGHT);
      canvasCtx.fillStyle = "#939393";

      const barWidth = 2;
      const spacing = 1;
      const maxBarHeight = HEIGHT * 0.8;
      const totalBarWidth = barWidth + spacing;
      const numBars = Math.floor(WIDTH / totalBarWidth);

      console.log('[🎵 WAVEFORM DEBUG] Drawing parameters:', {
        WIDTH,
        HEIGHT,
        numBars,
        dataArrayLength: dataArray.length,
        maxBarHeight,
        barWidth,
        canvasClientWidth: canvas.clientWidth,
        canvasOffsetWidth: canvas.offsetWidth
      });

      // 根据canvas宽度和数据长度，均匀分布绘制
      for (let i = 0; i < numBars; i++) {
        // 计算在数据数组中对应的索引（均匀采样）
        const dataIndex = Math.floor((i / numBars) * dataArray.length);
        const barHeight = Math.pow(dataArray[dataIndex] / 128.0, 8) * maxBarHeight;
        const x = totalBarWidth * i;
        const y = HEIGHT / 2 - barHeight / 2;
        canvasCtx.fillRect(x, y, barWidth, barHeight);
      }
    };

    const visualizeVolume = () => {
      const { analyser } = audioAnalyserRef.current;
      if (!analyser) return;

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const draw = () => {
        if (!isRecording) {
          cancelAnimationFrame(animationRef.current || 0);
          // Clear canvas when not recording
          if (canvasCtx) {
            const WIDTH = canvas.width;
            const HEIGHT = canvas.height;
            canvasCtx.clearRect(0, 0, WIDTH, HEIGHT);
          }
          return;
        }
        animationRef.current = requestAnimationFrame(draw);
        analyser.getByteTimeDomainData(dataArray);
        drawWaveform(dataArray);
      };

      draw();
    };

    if (isRecording && audioAnalyserRef.current.analyser) {
      visualizeVolume();
    } else {
      if (canvasCtx) {
        const WIDTH = canvas.width;
        const HEIGHT = canvas.height;
        canvasCtx.clearRect(0, 0, WIDTH, HEIGHT);
      }
      cancelAnimationFrame(animationRef.current || 0);
    }

    return () => {
      cancelAnimationFrame(animationRef.current || 0);
    };
  }, [isRecording, theme]);

  return (
    <div
      className={cn(
        "flex rounded-md relative w-full items-center gap-1 max-w-5xl",
        {
          "border p-1": isRecording,
          "border-none p-0": !isRecording,
        },
        // 🔑 默认高度为 h-16，但可以通过 className 覆盖
        !className?.includes('h-') && "h-16",
        className
      )}
          >
        {sessionConnectionStatus === SessionConnectionStatus.Connecting && (
          <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border border-blue-200 rounded-lg">
            <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
            <div className="flex flex-col">
              <span className="text-sm font-medium text-blue-700">正在连接</span>
              <span className="text-xs text-blue-600">请稍候...</span>
            </div>
          </div>
        )}
        {sessionConnectionStatus === SessionConnectionStatus.Disconnected && (
          <div className="flex items-center gap-2 px-4 py-2 bg-orange-50 border border-orange-200 rounded-lg">
            <WifiOff className="w-5 h-5 text-orange-500 animate-pulse" />
            <div className="flex flex-col">
              <span className="text-sm font-medium text-orange-700">连接已断开</span>
              <span className="text-xs text-orange-600">请检查网络连接</span>
            </div>
          </div>
        )}
        {sessionConnectionStatus === SessionConnectionStatus.Error && (
          <div className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 rounded-lg">
            <AlertTriangle className="w-5 h-5 text-red-500 animate-bounce" />
            <div className="flex flex-col">
              <span className="text-sm font-medium text-red-700">连接失败</span>
              <span className="text-xs text-red-600">请稍后重试</span>
            </div>
          </div>
        )}
      <canvas
        ref={canvasRef}
          className={`h-full flex-1 bg-background ${
          isRecording ? "flex" : "hidden"
        }`}
      />
      {/* 🔑 暂停按钮 - 移动端缩小 */}
      {isRecording && (
        <Button
          onClick={onPause}
          size={"icon"}
          disabled={isLoading}
          className="bg-red-800 hover:bg-red-900 text-white border-red-800 hover:border-red-900 rounded-full flex-shrink-0 size-7 sm:size-9"
        >
          {isLoading ? (
            <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          ) : (
            <Pause className="w-3 h-3 sm:w-4 sm:h-4" />
          )}
        </Button>
      )}
    </div>
  );
};


